/**
 * The switch schedule (switchLink.js) as a day-by-day cash model: what has
 * been billed, collected from the client and paid out to vendors / payroll on
 * any given day, the cash cushion between the two, and the people (man-hours
 * per week) doing the work. Pure - the React side only picks a day to read.
 *
 * Two payment modes, both over the same scope (the packets whose work is done):
 *  - sequenced:    what this project does - each vendor/payroll payment goes
 *                  out only after the client has paid for the packet that
 *                  earned it (the schedule's vendorPayDate);
 *  - conventional: the comparison - payroll paid every cycle for the days
 *                  worked, material vendors take a deposit at the start and
 *                  the rest net-30 after delivery, subcontractors net-30.
 * Collection from the client is the same in both, so the only thing that
 * differs is when money leaves - which is exactly what the cushion shows.
 */

import { addDays, diffDays, isoDateOnly } from "./switchLink";

export const PAYMENT_MODES = ["sequenced", "conventional"];

const CONVENTIONAL_TERMS = {
  depositShare: 0.3, // material vendors: share paid at PO / start of work
  netDays: 30, // balance (and subcontract) due this long after delivery
  payrollCycleDays: 14,
};

const EPOCH = "1970-01-01";
const MAX_DAYS = 3650; // a malformed far-off date must not allocate a decade of days

const laneOf = (payoutType) => (payoutType === "manhour" ? "payroll" : "vendor");

// The payroll run that covers a day's work: the next cycle boundary after it.
const payrollRunFor = (isoDate) => {
  const cycle = CONVENTIONAL_TERMS.payrollCycleDays;
  return addDays(EPOCH, (Math.floor(diffDays(EPOCH, isoDate) / cycle) + 1) * cycle);
};

const conventionalPayouts = (row, packet) => {
  const done = packet.workDoneDate;
  const start = packet.workStart && packet.workStart < done ? packet.workStart : done;
  const payout = (date, amount) => ({ date, code: row.code, type: laneOf(row.payoutType), amount });

  if (row.payoutType === "manhour") {
    const days = Math.max(1, diffDays(start, done));
    const byRun = new Map();
    for (let i = 0; i < days; i += 1) {
      const run = payrollRunFor(addDays(start, i));
      byRun.set(run, (byRun.get(run) || 0) + packet.cost / days);
    }
    return [...byRun].map(([date, amount]) => payout(date, amount));
  }
  const balanceDate = addDays(done, CONVENTIONAL_TERMS.netDays);
  if (row.payoutType === "subcontract") return [payout(balanceDate, packet.cost)];
  return [
    payout(start, packet.cost * CONVENTIONAL_TERMS.depositShare),
    payout(balanceDate, packet.cost * (1 - CONVENTIONAL_TERMS.depositShare)),
  ];
};

/** Every dated money movement of the rows, as { date, code, type, amount }.
 * type: bill | collect | vendor | payroll. */
const collectEvents = (rows, mode) => {
  const events = [];
  rows.forEach((row) => {
    const lane = laneOf(row.payoutType);
    row.packets.forEach((packet) => {
      if (!packet.switchDate) return;
      events.push({ date: packet.switchDate, code: row.code, type: "bill", amount: packet.value });
      events.push({
        date: packet.clientPayDate,
        code: row.code,
        type: "collect",
        lane,
        amount: packet.value,
        margin: packet.value - packet.cost,
      });
      if (mode === "sequenced") {
        events.push({ date: packet.vendorPayDate, code: row.code, type: lane, amount: packet.cost });
      } else {
        events.push(...conventionalPayouts(row, packet));
      }
    });
  });
  return events;
};

const hourSpansOf = (rows) =>
  rows
    .filter((row) => row.hours > 0 && row.workStart && row.workEnd)
    .map((row) => ({
      discipline: row.categoryName || "Other",
      start: row.workStart,
      days: Math.max(1, diffDays(row.workStart, row.workEnd)),
      hours: row.hours,
    }));

const cumulative = (daily) => {
  const out = new Array(daily.length);
  let running = 0;
  daily.forEach((amount, day) => {
    running += amount;
    out[day] = running;
  });
  return out;
};

const EMPTY_TIMELINE = {
  origin: null,
  days: 0,
  todayDay: 0,
  dayIndex: [],
  events: [],
  series: null,
  weeks: [],
  disciplines: [],
};

/**
 * @param schedule computeSwitchSchedule() / buildDemoSwitchSchedule() result
 * @param mode     one of PAYMENT_MODES
 * @param excluded Set of line codes taken out of scope
 * @returns day-indexed cumulative series (index 0 = `origin`), the events
 *          (with their `day`), weekly hours by discipline, and the totals of
 *          the lines in scope.
 */
export const buildCashTimeline = (schedule, { mode = "sequenced", excluded = new Set(), now = new Date() } = {}) => {
  const rows = schedule.filter((row) => !excluded.has(row.code));
  const totals = rows.reduce(
    (sum, row) => ({
      contract: sum.contract + row.value,
      cost: sum.cost + row.cost,
      vendorCost: sum.vendorCost + (laneOf(row.payoutType) === "vendor" ? row.cost : 0),
      payrollCost: sum.payrollCost + (laneOf(row.payoutType) === "payroll" ? row.cost : 0),
    }),
    { contract: 0, cost: 0, vendorCost: 0, payrollCost: 0 },
  );

  const events = collectEvents(rows, mode).filter((event) => event.date);
  const spans = hourSpansOf(rows);
  const todayIso = isoDateOnly(now);

  const dates = [
    ...events.map((event) => event.date),
    ...spans.flatMap((span) => [span.start, addDays(span.start, span.days)]),
  ];
  if (dates.length === 0) return { ...EMPTY_TIMELINE, totals };

  dates.push(todayIso);
  const origin = dates.reduce((a, b) => (a <= b ? a : b));
  const lastDate = dates.reduce((a, b) => (a >= b ? a : b));
  const days = Math.min(MAX_DAYS, diffDays(origin, lastDate) + 7);
  const dayOf = (isoDate) => Math.min(days, Math.max(0, diffDays(origin, isoDate)));

  const daily = {
    billed: new Array(days + 1).fill(0),
    collected: new Array(days + 1).fill(0),
    collectedVendor: new Array(days + 1).fill(0),
    collectedPayroll: new Array(days + 1).fill(0),
    vendorPaid: new Array(days + 1).fill(0),
    payrollPaid: new Array(days + 1).fill(0),
    margin: new Array(days + 1).fill(0),
  };
  const dayEvents = events.map((event) => ({ ...event, day: dayOf(event.date) }));
  dayEvents.forEach(({ day, type, lane, amount, margin }) => {
    if (type === "bill") daily.billed[day] += amount;
    if (type === "collect") {
      daily.collected[day] += amount;
      daily.margin[day] += margin;
      daily[lane === "payroll" ? "collectedPayroll" : "collectedVendor"][day] += amount;
    }
    if (type === "vendor") daily.vendorPaid[day] += amount;
    if (type === "payroll") daily.payrollPaid[day] += amount;
  });

  const series = Object.fromEntries(
    Object.entries(daily).map(([key, values]) => [key, cumulative(values)]),
  );
  series.paidOut = series.vendorPaid.map((vendor, day) => vendor + series.payrollPaid[day]);
  series.cushion = series.collected.map((collected, day) => collected - series.paidOut[day]);

  const disciplines = [...new Set(spans.map((span) => span.discipline))];
  const weeks = Array.from({ length: Math.ceil((days + 1) / 7) }, (_, week) => ({
    startDay: week * 7,
    hours: Object.fromEntries(disciplines.map((name) => [name, 0])),
  }));
  spans.forEach((span) => {
    const perDay = span.hours / span.days;
    const first = dayOf(span.start);
    for (let i = 0; i < span.days && first + i <= days; i += 1) {
      weeks[Math.floor((first + i) / 7)].hours[span.discipline] += perDay;
    }
  });

  return {
    origin,
    days,
    todayDay: dayOf(todayIso),
    dayIndex: Array.from({ length: days + 1 }, (_, day) => day),
    events: dayEvents,
    series,
    weeks,
    disciplines,
    totals,
  };
};

export const dayToIso = (timeline, day) => (timeline.origin ? addDays(timeline.origin, day) : null);

export const formatDay = (timeline, day) => {
  const iso = dayToIso(timeline, day);
  return iso
    ? new Date(`${iso}T00:00:00.000Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
        timeZone: "UTC",
      })
    : "";
};

/** Where the money stands on `day` - the flow strip's figures. */
export const positionOn = (timeline, day) => {
  const { series } = timeline;
  if (!series) return null;
  return {
    billed: series.billed[day],
    collected: series.collected[day],
    cushion: series.cushion[day],
    vendorPaid: series.vendorPaid[day],
    payrollPaid: series.payrollPaid[day],
    margin: series.margin[day],
  };
};

/** Month-end snapshots of the cumulative in / out, for the table under the
 * charts (the last day stands in for its month's end). */
export const monthlyCashRows = (timeline) => {
  if (!timeline.series) return [];
  const rows = [];
  for (let day = 0; day <= timeline.days; day += 1) {
    const iso = dayToIso(timeline, day);
    const isMonthEnd = day === timeline.days || addDays(iso, 1).slice(8, 10) === "01";
    if (!isMonthEnd) continue;
    rows.push({
      label: new Date(`${iso}T00:00:00.000Z`).toLocaleDateString("en-US", {
        month: "short",
        year: "2-digit",
        timeZone: "UTC",
      }),
      cashIn: timeline.series.collected[day],
      cashOut: timeline.series.paidOut[day],
      diff: timeline.series.cushion[day],
    });
  }
  return rows;
};

/** Headline figures over the whole timeline. */
export const timelineSummary = (timeline, schedule) => {
  const cushion = timeline.series?.cushion ?? [];
  const earnToPayout = schedule
    .flatMap((row) => row.packets)
    .filter((packet) => packet.workDoneDate && packet.vendorPayDate)
    .map((packet) => diffDays(packet.workDoneDate, packet.vendorPayDate));

  return {
    contract: timeline.totals.contract,
    plannedMargin: timeline.totals.contract - timeline.totals.cost,
    peakCushion: cushion.length ? Math.max(...cushion) : null,
    lowestCushion: cushion.length ? Math.min(...cushion) : null,
    avgEarnToPayoutDays: earnToPayout.length
      ? earnToPayout.reduce((sum, days) => sum + days, 0) / earnToPayout.length
      : null,
  };
};

export const LINE_STATUS = {
  removed: "Removed from scope",
  noTask: "No task linked",
  planned: "Planned",
  inProgress: "In progress",
  awaitingApproval: "Awaiting client approval",
  billed: "Billed · client paying",
  collected: "Collected · settling",
  settled: "Settled",
  funding: "Chinook funding",
};

/**
 * Where one line stands on `day`: how many of its switches have fired, what
 * has been collected for it and paid out for it, and a status label (a
 * LINE_STATUS key).
 */
export const lineStateOn = (row, timeline, day, isExcluded) => {
  const firedCount = timeline.origin
    ? row.packets.filter((packet) => packet.switchDate && packet.switchDate <= dayToIso(timeline, day)).length
    : 0;
  if (isExcluded) return { status: "removed", firedCount: 0, collected: 0, paid: 0 };

  let collected = 0;
  let paid = 0;
  timeline.events.forEach((event) => {
    if (event.code !== row.code || event.day > day) return;
    if (event.type === "collect") collected += event.amount;
    if (event.type === "vendor" || event.type === "payroll") paid += event.amount;
  });

  const iso = dayToIso(timeline, day);
  const between = (from, to) => from && iso && from <= iso && (!to || iso < to);
  const status = (() => {
    if (paid > collected + 1) return "funding";
    if (row.doneCount === row.packets.length && collected > 0 && paid >= row.cost - 1) return "settled";
    if (collected > paid + 1) return "collected";
    if (row.packets.some((p) => between(p.switchDate, p.clientPayDate))) return "billed";
    if (row.packets.some((p) => between(p.workDoneDate, p.switchDate))) return "awaitingApproval";
    if (row.progress == null) return "noTask";
    if (row.workStart && iso && iso < row.workStart) return "planned";
    return "inProgress";
  })();

  return { status, firedCount, collected, paid };
};
